import {AfterViewInit, Component, HostListener, OnDestroy, OnInit, output, Output, ViewChild} from '@angular/core';
import {LocalizeRouterPipe, LocalizeRouterService} from "@gilsdav/ngx-translate-router";
import {MatMenuModule} from "@angular/material/menu";
import {MatButtonModule} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatListItem} from "@angular/material/list";
import {Router, RouterLink} from "@angular/router";
import {Subscription} from "rxjs";
import {LanguageService} from "../../shared/services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import { forkJoin } from 'rxjs';
import {NgClass} from "@angular/common";
import {AuthService} from "../../shared/services/auth.service";
import {SnackbarService} from "../../shared/services/snackbar.service";

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [MatMenuModule, MatButtonModule, MatIcon, MatListItem, RouterLink, LocalizeRouterPipe, TranslateModule, NgClass],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit, OnDestroy {

  public availableLanguages = [
    { label: 'English', value: 'en' },
    { label: 'German', value: 'de' }
  ];

  userInfo: any;

  isSidebarVisible: boolean = false;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;
  private subscriptions: Subscription = new Subscription();

  onSidebarChange = output<boolean>()

  previousScrollPosition: number = 0;
  isHeaderHidden: boolean = false;


  constructor(
    private _translateService: TranslateService,
    private _localizeRouterService: LocalizeRouterService,
    private _languageService: LanguageService,
    private _router: Router,
    private authService: AuthService,
    private _snackbarService: SnackbarService) {
  }

  /**
   * Listen to the scroll event on window and perform actions accordingly.
   *
   * @event onWindowScroll
   * @returns {void}
   */
  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    if (currentScrollPosition > this.previousScrollPosition) {
      this.isHeaderHidden = true;
    } else {
      this.isHeaderHidden = false;
    }

    this.previousScrollPosition = currentScrollPosition;
  }



  ngOnInit(): void {
    // Prüfen, ob der Benutzer authentifiziert ist
    if (this.authService.isAuthenticated()) {
      this.authService.getUserInfo().subscribe();
    } else {
      // Falls nicht eingeloggt, kann hier alternativ eine leere Information gesetzt oder ein anderer Flow gestartet werden.
      console.log('User is not authenticated, skip getUserInfo.');
    }

    // Bestehende Logik:
    this.authService.userInfo$.subscribe(info => {
      this.userInfo = info;
    });

    this.setupLanguageSubscription()

    if(window.innerWidth >= 960) {
      this.isSidebarVisible = true;
      this.onSidebarChange.emit(this.isSidebarVisible);
    } else {
      this.isSidebarVisible = false;
      this.onSidebarChange.emit(this.isSidebarVisible);
    }
  }


  /**
   * Set up the language subscription.
   * This method sets the current language, sets the available language labels,
   * and subscribes to the language change event to update the current language
   * and the available language labels accordingly.
   *
   * @return {void}
   */
  private setupLanguageSubscription(): void{
    this.currentLanguage = this._languageService.getLanguage();
    this.setAvailableLanguagesLabels();
    this.subscriptions.add(
      this.langChangeSubscription = this._translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
        this.setAvailableLanguagesLabels();
      }));
  }

  /**
   * Sets the labels for the available languages.
   *
   * @private
   * @returns {void}
   */
  private setAvailableLanguagesLabels() :void{
    forkJoin({
      english: this._translateService.get('COMMON.english'),
      german: this._translateService.get('COMMON.german')
    }).subscribe(translations => {
      this.availableLanguages[0] = {
        label: translations.english,
        value: 'en'
      };
      this.availableLanguages[1] = {
        label: translations.german,
        value: 'de'
      };
    });
  }

  get isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  goToHome(): void {
    const currentLang = this._translateService.currentLang || 'en';
    this._router.navigate([`/${currentLang}`]);
  }

  logout(): void {
    this.authService.clearTokens();
    const currentLang = this._translateService.currentLang || 'en';
    this._router.navigate([`/${currentLang}`]);
    this._snackbarService.show('Logged out successfully');
  }

  /**
   * Switches the language of the application.
   *
   * @param {string} lang - The desired language code to switch to.
   * @returns {void}
   */
  switchLanguage(lang: string):void {
    this._localizeRouterService.changeLanguage(lang);
  }

  /**
   * Toggles the visibility of the sidebar.
   *
   * @returns {void}
   */
  toggleSidenav():void {
    this.isSidebarVisible = !this.isSidebarVisible;
    this.onSidebarChange.emit(this.isSidebarVisible);
  }

  /**
   * Lifecycle hook that is called when a component is about to be destroyed.
   * It is generally used to cleanup any resources such as subscriptions or event listeners.
   *
   * @return {void}
   */
  ngOnDestroy():void{
    this.subscriptions.unsubscribe();
  }
}
